/**
 * manage.js
 */

import React from 'react';
import axios from 'axios';
import {
  Grid,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { stripePromise } from 'stripe';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import MenuDrawer from 'components/menu-drawer';
import BasicDrawerMenu from 'components/menu-drawer/menu/basic-drawer-menu';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import DrawerContent from 'components/menu-drawer/drawer-content';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import { EditorIconSection } from 'components/icon-section/icon-section';
import Icon from 'components/icon';
import Button from 'components/button';
import Select from 'components/form-inputs/select';
import ConfirmDialog from 'components/confirm-dialog';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import FeatureBox from 'components/feature-box';
import {
  SubscriptionType,
  SubscriptionTypeName,
  PaymentType,
  PaymentTypeName,
  SubscriptionStatus,
  SubscriptionPrice
} from '../billing-constants';
import { ReactComponent as BillingImage } from 'assets/images/billing.svg';


// const useStyles = makeStyles(theme => ({
  
// }));


const BillingDetails = (props) => {
  const {
    billingUser,
    subscriptionData,
    setSubscriptionData,
    onClose,
    drawerState,
  } = props;
  // const classes = useStyles();
  const { user, setUser } = React.useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('billingEdit');
  const title = 'Edit Billing';
  const handlerSubscription = subscriptionData?.subscriptions.find(s => s.role.id === Types.Role.HANDLER);
  const [confirmModifySub, setConfirmModifySub] = React.useState();
  const [confirmCancelSub, setConfirmCancelSub] = React.useState();
  const [submissionResult, setSubmissionResult] = React.useState();
  const hasHandlerRole = Boolean((billingUser || user).availableRoles.find(r => r.id === Types.Role.HANDLER));

  // subscription fields
  const subType = handlerSubscription?.type;
  const paymentType = handlerSubscription?.paymentType;
  const status = handlerSubscription?.status;
  const balance = handlerSubscription?.balance || 0;
  const isInTrial = subType?.id === SubscriptionType.TRIAL;
  const trialTransitionReady = isInTrial && status?.id !== SubscriptionStatus.CANCELED &&
    (
      (paymentType?.id === PaymentType.AUTOMATIC && handlerSubscription.hasDefaultPayment) ||
      paymentType?.id === PaymentType.REMITTANCE
    );

  // hook form
  const formContext = useForm({ defaultValues: { type: '', paymentType: '' }});
  const { formState, handleSubmit, control, reset, setValue } = formContext;
  const subTypeSelection = useWatch({ control, name: 'type' });
  const subPaymentTypeSelection = useWatch({ control, name: 'paymentType' });

  // form func  
  const canSave = (
    balance === 0
    && (formState.isDirty || status?.id === SubscriptionStatus.CANCELED)
  );  
  const canUpdateOptions = balance === 0;


  React.useEffect(() => {
    if (handlerSubscription) {
      reset({
        // if trial and expired -> show empty
        // else show actual sub type
        type: (isInTrial && status?.id === SubscriptionStatus.CANCELED)
          ? ''
          : subType?.id || '',
        paymentType: isInTrial
          ? ''
          : paymentType?.id || ''
      });
    }
  }, [handlerSubscription, isInTrial, paymentType?.id, reset, status?.id, subType?.id]);


  React.useEffect(() => {
    // Once we have a submission result, if a paymentSessionId has been set, it means we should redirect to checkout 
    // immediately. Otherwise just close the drawer.
    // (The state -> effect intermediate step is so we can disable the ConfirmUnsavedEditor warning before redirecting.)

    if (submissionResult !== undefined) {
      if (submissionResult.paymentSessionId) {
        (async () => {
          const stripe = await stripePromise;
          const { error } = await stripe.redirectToCheckout({
            sessionId: submissionResult.paymentSessionId,
          });

          if (error) {
            enqueueSnackbar(`Error loading payment form. ${error.message}`, {
              variant: 'error',
              persist: true,
              anchorOrigin: {
                vertical: "top",
                horizontal: "center"
              }
            });
          }
        })();  
      } else {
        onClose();
      }
    }
  }, [enqueueSnackbar, onClose, submissionResult]);


  const summary = () => {
    // NO SUBSCRIPTION
    if (!handlerSubscription) {
      return <>
        Your account is inactive because you haven't chosen a subscription. 
        Update the settings below and then press Save to start using the handler role.
      </>
    }

    // TRIAL
    else if (isInTrial) {
      // CANCELED
      if (status.id === SubscriptionStatus.CANCELED) {
        return <>
          Your trial subscription has expired and your records are read-only. 
          To restore full access to your account you'll need a paid plan. 
          Set your desired subscription settings below and then press Reactivate.
        </>;
      } 
      // TRANSITION READY
      else if (trialTransitionReady) {
        return <>
          Your trial subscription will end soon. You're set up to transition 
          to a paid plan once the trial period ends.
        </>;
      }
      // TRANSITION NOT SETUP
      else {
        return <>
          You have a trial subscription which will expire soon. 
          To upgrade to a paid plan set your desired subscription settings below and then press Save.
        </>;
      }
    } 

    // HANDLER INVOICE
    else if (paymentType.id === PaymentType.REMITTANCE) {
      // CANCELED WITH BALANCE
      if (status.id === SubscriptionStatus.CANCELED && balance > 0) {
        return <>
          Your subscription has been canceled and your records are read-only. 
          Pay the outstanding balance to reactivate your subscription and restore 
          full access to your records.
          Subscription settings may not be changed until your balance is paid. 
          Pay your balance using the instructions on the Billing page.
        </>;
      }
      // CANCELED WITH NO BALANCE
      if (status.id === SubscriptionStatus.CANCELED && balance === 0) {
        return <>
          Your subscription has been canceled and your records are read-only. 
          To start a new subscription and restore full access to your account 
          just update the settings below and then press Reactivate.
        </>;
      }
      // PAYMENT DUE
      else if (balance > 0 || status.id === SubscriptionStatus.OVERDUE) {
        return <>
          Your subscription payment is due. 
          Subscription settings may not be changed until the invoice is paid. 
          Pay your invoice using the instructions on the Billing page. 
        </>;
      } 
      // ACTIVE
      else {
        return <>
          You have an invoiced subscription. 
          If you want to change your subscription then update the settings below and press Save.
        </>;
      }
    }

    // HANDLER AUTOMATIC
    else if (paymentType.id === PaymentType.AUTOMATIC) {
      // CANCELED WITH BALANCE
      if (status.id === SubscriptionStatus.CANCELED && balance > 0) {
        return <>
          Your subscription has been canceled and your records are read-only. 
          Pay the outstanding balance to reactivate your subscription and restore 
          full access to your records.
        </>;
      } 
      // CANCELED WITH NO BALANCE
      else if (status.id === SubscriptionStatus.CANCELED && balance === 0) {
        return <>
          Your subscription has been canceled and your records are read-only. 
          To start a new subscription and restore full access to your account 
          just update the settings below and then press Reactivate.
        </>;
      }
      // OVERDUE
      else if (status.id === SubscriptionStatus.OVERDUE) {
        return <>
          Your latest credit card payment failed and your subscription is overdue. 
          Subscription settings may not be changed until your credit card information is updated from the Billing page.
        </>;
      } 
      // ACTIVE
      else {
        return <>
          You have a credit card subscription which is charged automatically when your next subscription cycle starts. 
          If you want to change your subscription then update the settings below and press Save.
        </>;
      }
    }
  }


  const modificationText = (type, paymentType) => {
    let terms = {};
    if (type === SubscriptionType.YEARLY) {
      terms = {
        name: SubscriptionTypeName[SubscriptionType.YEARLY],
        each: 'year',
        price: SubscriptionPrice.YEARLY
      };
    } else if (type === SubscriptionType.MONTHLY) {
      terms = {
        name: SubscriptionTypeName[SubscriptionType.MONTHLY],
        each: 'month',
        price: SubscriptionPrice.MONTHLY
      };
    }

    if (type === SubscriptionType.TRIAL) {
      return <>
        You're about to start a Trial subscription which lasts for 30 days. 
        When the trial is over your account will become read-only unless you upgrade to a paid plan or add your credit card as a default payment method. 
        Click OK to confirm.
      </>;
    } else if (paymentType === PaymentType.REMITTANCE) {
      return <>
        <>You've selected {terms.name.startsWithVowel() ? 'an' : 'a'} {terms.name} Invoice subscription. </>
        {isInTrial && status?.id !== SubscriptionStatus.CANCELED
          ? <>At the end of your trial period you will be invoiced for ${terms.price} each {terms.each} and will have 30 days to pay. </>
          : <>You will be invoiced for ${terms.price} each {terms.each} and will have 30 days to pay. If you've already paid for the current subscription interval then you won't be asked to pay again, and changes will take effect at the end of the current billing cycle. </>
        }
        Click OK to confirm.
      </>;
    } else if (paymentType === PaymentType.AUTOMATIC) {
      return <>
        <>You've selected {terms.name.startsWithVowel() ? 'an' : 'a'} {terms.name} subscription which is paid automatically by credit card. </>
        {isInTrial && status?.id !== SubscriptionStatus.CANCELED
          ? <>You will be charged ${terms.price} at the end of your trial period and each {terms.each} on your billing date. </>
          : <>You will be charged ${terms.price} now and each {terms.each} on your billing date. If you've already paid for the current subscription interval then you won't be asked to pay again, and changes will take effect at the end of the current billing cycle. </>
        }        
        Click OK to confirm.
      </>;
    } else {
      return '';
    }
  }


  const billingRequest = async (request, data, msgSuccess, msgError) => {
    request = billingUser ? `${request}?userId=${billingUser.id}` : request;
    drawerState.loading.content.set(true);
    try {
      const response = await (axios({
        method: 'POST',
        url: request,
        data,
        bypassErrorHandling: true
      }));
      if (response.status !== 200) {
        throw new Error('');
      } else if (response.data.paymentSessionId) {
        setSubmissionResult({paymentSessionId: response.data.paymentSessionId});
      } else {
        const data = response.data;
        if (data.user && data.user.id === user.id) {
          setUser(data.user);
        }
        if (data.subscription) {
          setSubscriptionData(data.subscription);
          const handlerSub = data.subscription.subscriptions.find(s => s.role.id === Types.Role.HANDLER);
          reset({
            type: handlerSub?.type?.id || '',
            paymentType: handlerSub?.paymentType?.id || ''
          });
        }        
        enqueueSnackbar(msgSuccess, { variant: 'success' });
        setSubmissionResult({});  // An empty object is an indication that submission succeeded with no special action required
      }
    } catch(e) {
      enqueueSnackbar(`${msgError} ${e.message}`, {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
    } finally {
      drawerState.loading.content.set(false);
    }
  }


  const onSave = async (data) => {
    if (await confirmModifySub()) {
      billingRequest('/billing', data, 'Subscription saved.', 'Error saving subscription.');
    }
  };


  const onCancel = async () => {
    if (await confirmCancelSub()) {
      billingRequest('/billing/cancel', null, 'Subscription canceled', 'Error canceling subscription.');
    }
  }


  const saveButton = (
    <Tooltip
      arrow
      placement='top'
      title={balance > 0 
        ? <>Subscription settings may not be<br />changed until your balance is paid</>
        : ''}
    >
      <span>
        <DrawerActionButton
          level={1}
          disabled={!canSave}
          onClick={() => handleSubmit(onSave)()}      
          children={status?.id === SubscriptionStatus.CANCELED ? 'Reactivate' : 'Save'}
        />
      </span>
    </Tooltip>
  );


  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      onClick={onClose}
      size="small"
      style={{ color: 'inherit' }}
      children={<Icon type='close' />}
    />
  )
  

  return (
    <>
      <DrawerContentBuilder
        mobileAppBar={{
          title,
          leftActions: closeButton,
          rightActions: saveButton
        }}
        desktopHeader={{
          title,
          actions: saveButton
        }}
        children={(
          <FormProvider {...formContext}>
            <form>
              <DrawerContent>
                <FeatureBox loading={!subscriptionData} contentLoadingOpacity={0} style={{ minHeight: 80 }}>
                  <EditorIconSection
                    iconName='billing-edit'
                    iconMarginTop={4}
                    helpProps={{
                      translation: 'billingEdit',
                      section: 'general'
                    }}
                  >
                    <Typography variant='subtext-large' sx={{ mb: 2 }}>
                      {summary()}
                      {((!isInTrial && handlerSubscription?.pendingType) || trialTransitionReady) &&
                        <Alert severity='warning' sx={{ marginTop: 2, marginBottom: 2 }}>
                          <>Your current subscription settings are shown below. Note that you have pending changes that </>
                          <>will take effect at the end of the {trialTransitionReady ? 'trial period' : 'current billing cycle'}: transition to </>
                          <strong>{PaymentTypeName[handlerSubscription.paymentType.id].toLowerCase()} {SubscriptionTypeName[handlerSubscription.pendingType.id].toLowerCase()} </strong>
                          <>billing. Editing your subscription settings will replace those changes.</>
                        </Alert>
                      }
                    </Typography>

                    {hasHandlerRole &&
                    <>
                      {/* SUBSCRIPTION TYPE */}
                      <HelpTextItem
                        sidebarText={drawerState.sidebarText}
                        title={t('general.subscriptionType.helpTitle')}
                        text={t('general.subscriptionType.helpInfo')}
                        style={{ marginBottom: 8 }}
                      >
                        {focusHandlers => 
                          <Grid container spacing={2}>
                            <FormGridItem>
                              <Select
                                name='type'
                                label='Subscription Type'
                                focusHandlers={focusHandlers}
                                disabled={!canUpdateOptions}
                                options={[
                                  ...(!handlerSubscription
                                    || (subType?.id === SubscriptionType.TRIAL
                                      && (status?.id !== SubscriptionStatus.CANCELED || handlerSubscription.isTrialEligible))                             
                                    ? [{ 
                                      id: SubscriptionType.TRIAL, 
                                      name: 'Free Trial', 
                                    }]
                                    : []),
                                  { id: SubscriptionType.MONTHLY, name: 'Monthly' },
                                  { id: SubscriptionType.YEARLY, name: 'Annual' },
                                ]}
                                rules={{
                                  required: 'Please select a subscription type'
                                }}
                                onChange={(e, value) => {
                                  if ([SubscriptionType.MONTHLY, SubscriptionType.TRIAL].includes(value)) {
                                    setValue('paymentType', PaymentType.AUTOMATIC);
                                  }
                                }}
                              />
                            </FormGridItem>
                          </Grid>}
                      </HelpTextItem>

                      {/* PAYMENT TYPE */}
                      <HelpTextItem
                        sidebarText={drawerState.sidebarText}
                        title={t('general.paymentType.helpTitle')}
                        text={t('general.paymentType.helpInfo')}
                      >
                        {focusHandlers => 
                          <Grid container spacing={2}>
                            <FormGridItem>
                              <Select
                                name='paymentType'
                                label='Payment Type'
                                focusHandlers={focusHandlers}
                                disabled={!canUpdateOptions || [SubscriptionType.MONTHLY, SubscriptionType.TRIAL].includes(subTypeSelection)}
                                options={[
                                  { id: PaymentType.REMITTANCE, name: PaymentTypeName[PaymentType.REMITTANCE] },
                                  { id: PaymentType.AUTOMATIC, name: PaymentTypeName[PaymentType.AUTOMATIC]} 
                                ]}
                                rules={{
                                  validate: val => {
                                    return subTypeSelection !== SubscriptionType.TRIAL && val === ''
                                      ? 'Please select a payment type'
                                      : true;
                                  }
                                }}
                              />
                            </FormGridItem>
                          </Grid>}
                      </HelpTextItem>
                    </>}
                  </EditorIconSection>

                  {/* CANCEL */}
                  {((status?.id && status?.id !== SubscriptionStatus.CANCELED)) &&
                  <EditorIconSection
                    iconName='cancel-subscription'
                    iconMarginTop={6}
                    helpProps={{
                      translation: 'billingEdit',
                      section: 'cancelSubscription'
                    }}
                  >
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('cancelSubscription.cancel.helpTitle')}
                      text={t('cancelSubscription.cancel.helpInfo')}
                    >
                      {focusHandlers => 
                        <Grid container spacing={2}>
                          <FormGridItem>
                            <Button
                              variant='contained'
                              onClick={onCancel}
                              children='Cancel Subscription'
                            />
                          </FormGridItem>
                        </Grid>}
                    </HelpTextItem>
                  </EditorIconSection>}
                </FeatureBox>
              </DrawerContent>
            </form>
          </FormProvider>
        )}
      />

      {/* MODIFICATIONS CONFIRM DIALOG */}
      <ConfirmDialog
        titleStyle='warning'
        title={`Subscription Update`}
        titleIcon='warning'
        setConfirm={setConfirmModifySub}
        content={modificationText(subTypeSelection, subPaymentTypeSelection)}
      />

      {/* CANCEL CONFIRM DIALOG */}
      <ConfirmDialog
        titleStyle='error'
        title={`Subscription Cancellation`}
        titleIcon='warning'
        setConfirm={setConfirmCancelSub}
        content={<>
          Are you sure you want to cancel this subscription?
          This handler account will become read-only but you'll still be able to login to view records and create reports. 
        </>}
      />

      {/* CONFIRM UNSAVED CHANGES DIALOG */}
      <ConfirmUnsavedEditor
        dirty={formState.isDirty && submissionResult === undefined}
        // discard={restoreOriginal}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                await onSave(data);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}


const BillingMenu = (props) => {
  return (
    <BasicDrawerMenu 
      items={[{
        key: 'billing',
        iconName: 'billing-edit',
        label: 'Edit Billing',
        path: '',
        component: BillingDetails,
        backgroundImage: <BillingImage />
      }]}
    />
  );
}


export default function BillingEdit(props) {
  const history = useHistory();
  const basePath = useRouteMatch();
  const drawerPath = '/edit';
  const drawerBasePath = basePath.url + drawerPath;


  const onCloseDrawer = () => {
    if (props.onClose) {
      props.onClose();
    }
    history.push(basePath.url);
  } 

  return (
    <MenuDrawer
      {...props}
      title='EDIT BILLING'
      className='billing-edit'
      drawerPath={drawerPath}
      basePath={basePath.url}
      menuRoutes={[{
        component: BillingMenu,
        path: [drawerBasePath]
      }]}
      desktopRoutes={[{
        component: BillingDetails,
        path: ''
      }]}
      mobileRoutes={[{
        component: BillingDetails,
        path: ''
      }]}
      onClose={onCloseDrawer}
    />
  )
}